import { SafeAreaView, SxProp, View, Image, styled, ScrollView } from 'dripsy';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { UniButton } from '@/design-system/components/buttons';
import { Icon } from '@/design-system/components/icons/Icon';
import { UniCheckbox } from '@/design-system/components/inputs';
import { H1, H5 } from '@/design-system/components/typography';
import { LayoutContext, ScreenSize } from 'contexts/LayoutContext';
import { useStore } from 'store/store';
import { PageContainer } from '../../components/page/PageContainer';
import { PageHeader } from '../../components/page/PageHeader';
import { LinearGradient } from 'expo-linear-gradient';
import { LayoutChangeEvent } from 'react-native';

interface DiscoverLandingScreenProps {
  loading: boolean;
  openAccount: () => void;
}

const Gradient = styled(LinearGradient, {
  themeKey: 'buttons',
  defaultVariant: 'base'
})({});

export const DiscoverLanding: React.FC<DiscoverLandingScreenProps> = ({ loading, openAccount }) => {
  const store = useStore();
  const layout = useContext(LayoutContext);
  const { t } = useTranslation('earn');
  const [isAcknowledged, setIsAcknowledged] = useState(false);

  return (
    <SafeAreaView sx={{ flex: 1, height: '100%', overflowY: 'scroll' }}>
      <PageContainer>
        <View sx={{ height: '$10' }} />
        <PageHeader title={t('earn-invest::glossary.earn-and-invest')} />
        <Gradient
          colors={['#1E304B', '#3D7AC5', '#3D7AC5', '#1E304B']}
          sx={{
            ...sxStyles.container,
            flexDirection: layout.screenSize === ScreenSize.DesktopFull ? 'row' : 'column',
            padding: layout.screenSize === ScreenSize.DesktopFull ? 0 : '$10'
          }}
          // diagonal
          start={{ x: 1, y: 0 }}
          end={{ x: 0, y: 1 }}>
          <Image
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              height: '100%',
              width: '100%'
            }}
            source={require('assets/images/discover_landing_background_pattern_web.png')}
          />
          {/* Container Left */}
          <View sx={sxStyles.containerLeft}>
            <H1 bold sx={{ mb: '$5', color: '$textTheme' }}>
              {t('earn-invest::glossary.earn-and-invest', { vendor: store.vendor })}
            </H1>
            <H5 bold sx={{ mb: '$5', color: '$textDarkBackground', textAlign: 'left' }}>
              {t('earn-invest::glossary.subscription-to-offers')}
            </H5>
            <UniCheckbox
              value={isAcknowledged}
              onValueChange={() => setIsAcknowledged(!isAcknowledged)}
              sx={{ mb: '$10' }}
              label={t('earn-invest::earn-landing.text--application-confirmation', { vendor: store.brand })}
              options={{
                labelColor: '$textDarkBackground'
              }}
            />
            <View sx={sxStyles.buttonGroup}>
              <UniButton
                size="large"
                label={t('earn-invest::glossary.open-account')}
                onPress={openAccount}
                disabled={!isAcknowledged || loading}
                loading={loading}
                sx={{ width: 250 }}
              />
            </View>
          </View>
          {/* Container Right */}
          <View sx={sxStyles.containerRight}>
            <Icon name="landingPageWeb" height={349} width={631} />
          </View>
        </Gradient>
      </PageContainer>
    </SafeAreaView>
  );
};

const sxStyles: Record<string, SxProp> = {
  container: {
    flexGrow: 1,
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '$10',
    borderRadius: 15
  },
  containerLeft: {
    maxWidth: 550,
    justifyContent: 'center',
    alignItems: 'flex-start'
  },
  containerRight: {
    justifyContent: 'center',
    alignItems: 'center'
  },
  buttonGroup: {
    flexDirection: 'row',
    gap: '$10'
  }
};
